import { useCallback, useEffect, useRef, useState } from "react";

export const useKioskIdleTimer = ({
  enabled = true,
  idleAfterMs = 120000,
  warningSeconds = 10,
  onTimeout,
}) => {
  const idleTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [countdown, setCountdown] = useState(warningSeconds);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const runTimeout = useCallback(() => {
    clearAllTimers();
    setIsWarningOpen(false);
    onTimeoutRef.current?.();
  }, [clearAllTimers]);

  const openWarning = useCallback(() => {
    clearAllTimers();
    setIsWarningOpen(true);
    setCountdown(warningSeconds);

    let remain = warningSeconds;

    countdownTimerRef.current = setInterval(() => {
      remain -= 1;
      setCountdown(remain);

      if (remain <= 0) {
        runTimeout();
      }
    }, 1000);
  }, [clearAllTimers, runTimeout, warningSeconds]);

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    clearAllTimers();
    setIsWarningOpen(false);
    setCountdown(warningSeconds);

    idleTimerRef.current = setTimeout(() => {
      openWarning();
    }, idleAfterMs);
  }, [enabled, clearAllTimers, idleAfterMs, openWarning, warningSeconds]);

  const closeWarning = useCallback(() => {
    setIsWarningOpen(false);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setIsWarningOpen(false);
      return;
    }

    const activityEvents = ["pointerdown", "keydown", "touchstart"];

    const handleActivity = () => {
      resetTimer();
    };

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity);
    });

    resetTimer();

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      clearAllTimers();
    };
  }, [enabled, resetTimer, clearAllTimers]);

  return {
    isWarningOpen,
    countdown,
    resetTimer,
    closeWarning,
  };
};